import { vec3 } from 'gl-matrix';
import { RenderObject } from '../../object/RenderObject';
import { Agent } from './Agent';

/**
 * Result of a swept-circle vs agent intersection query.
 */
export interface AgentSweepHit {
  /** Parametric position along the swept segment, in [0, 1]. 0 = already overlapping at p0. */
  t: number;
  /** The agent that was hit. */
  agent: Agent;
}

/**
 * Dynamic-agent spatial registry. Holds every body-blocking entity in the
 * scene (player + NPCs) and answers:
 *
 *   - `sweepHorizontal(p0, p1, radius, self)` — does a moving capsule of
 *     `radius` swept from `p0` to `p1` hit any other agent's capsule?
 *     Returns the FIRST hit (smallest `t`) or null. Used by
 *     `PlayerControlBehaviour.tryMove` to clamp movement before walking
 *     through another agent.
 *
 *   - `nearby(x, z, radius, self)` — every agent whose XZ center is within
 *     `radius` of `(x, z)`. Used for AoE queries, perception, etc.
 *
 * Implementation: linear scan. At ~100 agents that's ~100 distance checks
 * per query — a few microseconds, well within budget for per-frame movement.
 * Once N grows into the thousands (large NPC populations, swarms), wrap a
 * spatial hash on top with the same public API. The brute-force version
 * has no bookkeeping cost as agents move — `position` is read live each
 * query, no per-frame dirty-marking needed.
 *
 * Y-disjoint test: agents whose vertical extents don't overlap (one on a
 * second-floor balcony, one on the ground) are skipped cheaply before the
 * XZ distance test. Cheap conservative filter — only blocks pairs that
 * actually share Y space.
 */
export class AgentRegistry {
  private readonly agents = new Set<Agent>();
  /**
   * Per-`RenderObject` agent index, so the multiple wiring points that try
   * to register an animated entity (e.g. `createNavigationAwareObject` for
   * the player path, `registerAnimated` for scene-loaded NPCs) all collapse
   * onto a single Agent instance per object.
   */
  private readonly byObject = new WeakMap<RenderObject, Agent>();

  /** Number of registered agents. */
  get size(): number {
    return this.agents.size;
  }

  add(agent: Agent): void {
    this.agents.add(agent);
  }

  remove(agent: Agent): void {
    this.agents.delete(agent);
  }

  has(agent: Agent): boolean {
    return this.agents.has(agent);
  }

  clear(): void {
    this.agents.clear();
  }

  /** Returns the agent (if any) registered for this RenderObject. */
  getForObject(obj: RenderObject): Agent | undefined {
    return this.byObject.get(obj);
  }

  /**
   * Ensure an agent exists for `obj`. Returns the existing agent if one is
   * already registered for it, or creates a new one with sensible defaults
   * derived from the object's MODEL-LOCAL AABB (`bounds.localMin`/
   * `localMax`) multiplied by `obj.trs.scale`. `opts.radius` / `opts.height`
   * override the derived defaults — useful when a model's bounding box is
   * wider than the actual body footprint (e.g. characters with outstretched
   * limbs).
   *
   * **Why not `bounds.boundingBox`?** That field is a cube inscribing the
   * model's bounding *sphere* (see `BoundingDimensions.update`) — its width,
   * height and depth are all equal, sized by the model's max dimension. So
   * a 1.8m-tall humanoid would yield a "1.8m wide" bounding box that has
   * nothing to do with body width. `localMin`/`localMax` are the real AABB.
   *
   * Idempotent — safe to call from multiple wiring points.
   */
  ensureForObject(
    obj: RenderObject,
    opts?: { radius?: number; height?: number },
  ): Agent {
    const existing = this.byObject.get(obj);
    if (existing) return existing;
    const lo = obj.bounds.localMin;
    const hi = obj.bounds.localMax;
    const scale = obj.trs.scale ?? 1;
    const halfX = ((hi[0] - lo[0]) / 2) * scale;
    const halfZ = ((hi[2] - lo[2]) / 2) * scale;
    // Smaller horizontal half-extent — wider axes typically include
    // outstretched limbs / weapons that shouldn't dictate body radius.
    const derivedRadius = Math.min(halfX, halfZ);
    const derivedHeight = (hi[1] - lo[1]) * scale;
    const agent: Agent = {
      position: obj.trs.position as vec3,
      radius: opts?.radius ?? Math.max(0.1, derivedRadius),
      height: opts?.height ?? Math.max(0.1, derivedHeight),
    };
    this.add(agent);
    this.byObject.set(obj, agent);
    return agent;
  }

  /**
   * Auto-register every animated RenderObject as an agent. Mirrors
   * `BoneHitDetection.registerAnimated` — same idempotent contract, same
   * call sites in the scene loader. Returns null for non-animated objects
   * (no-op). The agent's radius/height are derived from the object's
   * bounding box; pass explicit `opts` to override.
   */
  registerAnimated(
    obj: RenderObject,
    opts?: { radius?: number; height?: number },
  ): Agent | null {
    if (!obj.isAnimated) return null;
    return this.ensureForObject(obj, opts);
  }

  /** Iterate every registered agent. Read-only — don't mutate during iteration. */
  *all(): IterableIterator<Agent> {
    for (const a of this.agents) yield a;
  }

  /**
   * Find every agent whose XZ center is within `radius` of `(x, z)`, except
   * `self`. Skips agents whose Y range doesn't overlap `[y - 0.1, y + 2]`
   * (a 2m vertical window suitable for humanoid characters).
   */
  nearby(x: number, y: number, z: number, radius: number, self: Agent | null): Agent[] {
    const hits: Agent[] = [];
    const r2 = radius * radius;
    for (const a of this.agents) {
      if (a === self) continue;
      if (!verticallyOverlapping(y, 2, a.position[1], a.height)) continue;
      const dx = a.position[0] - x;
      const dz = a.position[2] - z;
      if (dx * dx + dz * dz <= r2) hits.push(a);
    }
    return hits;
  }

  /**
   * Sweep a capsule of `radius` from `p0` to `p1` against every registered
   * agent except `self`. Returns the nearest hit along the segment, or
   * `null` if the path is clear.
   *
   * Math: the agent's circle (XZ projection, radius = agent.radius) is
   * inflated by the moving capsule's `radius`, then checked against the
   * segment for closest approach. Solves the standard quadratic ray-vs-
   * circle test in 2D (XZ plane).
   */
  sweepHorizontal(
    p0: vec3,
    p1: vec3,
    radius: number,
    self: Agent | null,
  ): AgentSweepHit | null {
    const dx = p1[0] - p0[0];
    const dz = p1[2] - p0[2];
    const segLen2 = dx * dx + dz * dz;
    if (segLen2 < 1e-12) return null;

    // Y window from p0/p1 — a swept segment occupies a Y range. Treat the
    // moving capsule's height as roughly 2m (humanoid) for the disjoint test.
    const yLo = Math.min(p0[1], p1[1]);
    const yHi = Math.max(p0[1], p1[1]);

    let bestT = Infinity;
    let bestAgent: Agent | null = null;

    for (const a of this.agents) {
      if (a === self) continue;
      // Quick Y-disjoint check: the moving capsule's [yLo, yHi+2] vs the
      // agent's [a.position.y, a.position.y + a.height].
      if (!verticallyRangesOverlap(yLo - 0.1, yHi + 2, a.position[1], a.position[1] + a.height)) continue;

      const combined = radius + a.radius;
      const fx = p0[0] - a.position[0];
      const fz = p0[2] - a.position[2];
      // Quadratic a*t^2 + b*t + c = 0 where roots are the entry/exit of
      // the segment through the inflated circle around the agent.
      const ca = segLen2;
      const cb = 2 * (fx * dx + fz * dz);
      const cc = fx * fx + fz * fz - combined * combined;

      let tEnter: number;
      if (cc <= 0) {
        // Already overlapping at p0 — treat as a hit at t=0 so the caller
        // doesn't advance further into the agent.
        tEnter = 0;
      } else {
        const disc = cb * cb - 4 * ca * cc;
        if (disc < 0) continue; // no intersection
        const sqDisc = Math.sqrt(disc);
        tEnter = (-cb - sqDisc) / (2 * ca);
        if (tEnter < 0 || tEnter > 1) continue;
      }

      if (tEnter < bestT) {
        bestT = tEnter;
        bestAgent = a;
      }
    }

    return bestAgent === null ? null : { t: bestT, agent: bestAgent };
  }
}

/** Cheap overlap test on two vertical extents `[a, a + ah]` and `[b, b + bh]`. */
function verticallyOverlapping(ay: number, ah: number, by: number, bh: number): boolean {
  return ay < by + bh && by < ay + ah;
}

function verticallyRangesOverlap(aLo: number, aHi: number, bLo: number, bHi: number): boolean {
  return aLo < bHi && bLo < aHi;
}
